import React, { useMemo } from 'react';
import { Box, Typography, Paper, Stack, Chip, Tooltip } from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface WorkflowVisualizationProps {
  dagDefinition: any;
  /** Create-mode only: suggested schedule from intent (used to show schedule trigger icon before workflow is saved). */
  suggestedScheduleConfig?: any;
  /** Create-mode only: suggested webhook from intent (used to show webhook trigger icon before workflow is saved). */
  suggestedWebhookConfig?: any;
}

// ── Step type config ──────────────────────────────────────────────────────────

const STEP_CFG: Record<string, { icon: React.ReactNode; color: string; bg: string; label: string }> = {
  agent:       { icon: <SmartToyOutlinedIcon sx={{ fontSize: 20 }} />, color: '#B87333', bg: '#fdf4ec',  label: 'Agent'        },
  tool:        { icon: <BuildOutlinedIcon    sx={{ fontSize: 20 }} />, color: '#789769', bg: '#f2f6f0',  label: 'Tool'         },
  conditional: { icon: <AltRouteIcon        sx={{ fontSize: 20 }} />, color: '#D4A017', bg: '#fdf8e6',  label: 'Conditional'  },
  human_input: { icon: <PersonOutlineIcon   sx={{ fontSize: 20 }} />, color: '#8E588E', bg: '#f7f0f7',  label: 'Human Review' },
  loop:        { icon: <ReplayIcon          sx={{ fontSize: 20 }} />, color: '#4A90D9', bg: '#eef5fc',  label: 'Loop'         },
  merge:       { icon: <CallMergeIcon       sx={{ fontSize: 20 }} />, color: '#2E8B57', bg: '#edf6f1',  label: 'Merge'        },
  wait:        { icon: <HourglassEmptyIcon  sx={{ fontSize: 20 }} />, color: '#6B7B8D', bg: '#f2f4f6',  label: 'Wait'         },
  trigger:     { icon: <ScheduleIcon        sx={{ fontSize: 20 }} />, color: '#5A7D9A', bg: '#eef3f7',  label: 'Trigger'      },
};

function getCfg(type: string) {
  return STEP_CFG[type] ?? {
    icon: <HelpOutlineIcon sx={{ fontSize: 20 }} />,
    color: '#6B7B8D',
    bg: '#f2f4f6',
    label: type,
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatName(step: any): string {
  if (step.name) return step.name;
  return step.id
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c: string) => c.toUpperCase());
}

/** Assign a topological level (column index) to each step. Cycle-safe. */
function computeLevels(steps: any[]): Map<string, number> {
  const stepMap = new Map(steps.map((s: any) => [s.id, s]));
  const levels   = new Map<string, number>();
  const visiting = new Set<string>();

  function visit(id: string): number {
    if (levels.has(id))   return levels.get(id)!;
    if (visiting.has(id)) return 0; // cycle guard
    visiting.add(id);

    const step = stepMap.get(id);
    if (!step) { levels.set(id, 0); return 0; }

    const deps: string[] = (step.depends_on || []).filter((d: string) => stepMap.has(d));
    const level = deps.length === 0
      ? 0
      : Math.max(...deps.map(visit)) + 1;

    levels.set(id, level);
    visiting.delete(id);
    return level;
  }

  steps.forEach((s: any) => visit(s.id));
  return levels;
}

// ── Arrow connector ───────────────────────────────────────────────────────────

const Arrow: React.FC<{ width: number }> = ({ width }) => {
  const lineEnd  = width - 10;
  const arrowTip = width - 2;
  const arrowBack = width - 10;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <svg width={width} height={16} style={{ overflow: 'visible' }}>
        <line x1="2" y1="8" x2={lineEnd} y2="8" stroke="#cbd5e1" strokeWidth="1.5" />
        <path
          d={`M${arrowBack} 4 L${arrowTip} 8 L${arrowBack} 12`}
          fill="none"
          stroke="#cbd5e1"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </Box>
  );
};

// ── Step card ─────────────────────────────────────────────────────────────────

interface StepCardProps {
  step: any;
  bodySteps?: any[];
  /** smaller variant used inside loop zones */
  compact?: boolean;
}

const StepCard: React.FC<StepCardProps> = ({ step, bodySteps, compact = false }) => {
  const cfg  = getCfg(step.type);
  const name = formatName(step);
  const isLoopWithBody = step.type === 'loop' && bodySteps && bodySteps.length > 0;

  // Loop step: render a dashed zone containing the body steps + a loop label
  if (isLoopWithBody) {
    return (
      <Box
        sx={{
          border: `1.5px dashed ${cfg.color}`,
          borderRadius: 3,
          p: 1.5,
          pt: 2.5,
          bgcolor: `${cfg.color}08`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          position: 'relative',
          minWidth: 140,
        }}
      >
        {/* Zone label */}
        <Chip
          icon={<ReplayIcon sx={{ fontSize: '11px !important', color: '#fff !important' }} />}
          label={name}
          size="small"
          sx={{
            position: 'absolute',
            top: -11,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: cfg.color,
            color: '#fff',
            fontWeight: 800,
            fontSize: 9,
            height: 20,
            maxWidth: 160,
            '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
          }}
        />
        {/* Body steps stacked vertically */}
        <Stack spacing={2} alignItems="center">
          {bodySteps!.map((bs: any) => (
            <StepCard key={bs.id} step={bs} />
          ))}
        </Stack>
      </Box>
    );
  }

  const iconSize = compact ? 52 : 64;
  const width    = compact ? 100 : 120;

  return (
    <Tooltip title={`${name} (${cfg.label})`} placement="top" arrow>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          width,
          cursor: 'default',
        }}
      >
        {/* Icon box */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: iconSize,
            height: iconSize,
            borderRadius: compact ? 2.5 : 3,
            border: `2px solid ${cfg.color}55`,
            bgcolor: cfg.bg,
            transition: 'box-shadow 0.2s',
            '&:hover': { boxShadow: `0 0 0 4px ${cfg.color}22` },
          }}
        >
          <Box sx={{ color: cfg.color, lineHeight: 0, fontSize: compact ? 22 : 28, '& svg': { fontSize: 'inherit' } }}>
            {cfg.icon}
          </Box>
        </Paper>

        {/* Step name label below the box */}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: '#334155',
            textAlign: 'center',
            lineHeight: 1.3,
            fontSize: compact ? 10 : 11,
            maxWidth: width,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {name}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ── Trigger node ─────────────────────────────────────────────────────────────

type TriggerType = 'schedule' | 'webhook' | 'manual';

function getTriggerCfg(triggerType: TriggerType) {
  if (triggerType === 'schedule') return {
    icon: <ScheduleIcon sx={{ fontSize: 28 }} />,
    color: '#5A7D9A',
    bg: '#eef3f7',
    label: 'Schedule',
  };
  if (triggerType === 'webhook') return {
    icon: <CloudQueueIcon sx={{ fontSize: 28 }} />,
    color: '#2C3E50',
    bg: '#edf0f3',
    label: 'Webhook',
  };
  // manual trigger — no schedule/webhook configured
  return {
    icon: <ScheduleIcon sx={{ fontSize: 28 }} />,
    color: '#5A7D9A',
    bg: '#eef3f7',
    label: 'Trigger',
  };
}

const TriggerNode: React.FC<{ triggerType?: TriggerType }> = ({ triggerType = 'manual' }) => {
  const cfg = getTriggerCfg(triggerType);
  return (
    <Tooltip title={`${cfg.label} trigger`} placement="top" arrow>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          width: 120,
          cursor: 'default',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: 3,
            border: `2px solid ${cfg.color}55`,
            bgcolor: cfg.bg,
            transition: 'box-shadow 0.2s',
            '&:hover': { boxShadow: `0 0 0 4px ${cfg.color}22` },
          }}
        >
          <Box sx={{ color: cfg.color, lineHeight: 0, fontSize: 28, '& svg': { fontSize: 'inherit' } }}>
            {cfg.icon}
          </Box>
        </Paper>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: '#334155', textAlign: 'center', lineHeight: 1.3, fontSize: 11 }}
        >
          {cfg.label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const WorkflowVisualization: React.FC<WorkflowVisualizationProps> = ({ dagDefinition, suggestedScheduleConfig, suggestedWebhookConfig }) => {
  const { columns, loopBodies, hasSteps } = useMemo(() => {
    const steps: any[] = dagDefinition?.steps ?? [];
    // Strip any trigger/webhook/start/end steps — these are metadata, not DAG steps.
    // Also strip by id pattern: the LLM sometimes emits type="agent" steps whose id is
    // "webhook_trigger", "webhook", "trigger", "start", "end" etc.
    const INVALID_TYPES = new Set(['trigger', 'webhook', 'start', 'end', 'schedule']);
    const isInvalid = (s: any) => INVALID_TYPES.has(String(s.type ?? '').toLowerCase());

    const topLevel: any[] = steps.filter((s: any) => !s.scope && !isInvalid(s));
    const scoped:   any[] = steps.filter((s: any) =>  s.scope && !isInvalid(s));

    // Loop body map: loopStepId → body steps
    const loopBodies = new Map<string, any[]>();
    scoped.forEach((s: any) => {
      if (!loopBodies.has(s.scope)) loopBodies.set(s.scope, []);
      loopBodies.get(s.scope)!.push(s);
    });

    // Assign topological levels and bucket into columns
    const levels   = computeLevels(topLevel);
    const maxLevel = Math.max(0, ...Array.from(levels.values()));
    const columns: any[][] = Array.from({ length: maxLevel + 1 }, () => []);
    topLevel.forEach((s: any) => {
      columns[levels.get(s.id) ?? 0].push(s);
    });

    return { columns, loopBodies, hasSteps: topLevel.length > 0 || scoped.length > 0 };
  }, [dagDefinition]);

  if (!hasSteps) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">No steps to display</Typography>
      </Box>
    );
  }

  // Adaptive arrow width: more space for fewer columns, tighter for many
  const arrowWidth = Math.round(Math.max(36, Math.min(100, 240 / Math.max(columns.length, 1))));

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        '&::-webkit-scrollbar': { height: 6, width: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 10 },
      }}
    >
      {/* Horizontal flow row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
        {/* Trigger nodes — shown for every trigger that is configured or suggested.
            Both schedule AND webhook can be active simultaneously. */}
        {(() => {
          const triggerTypes: TriggerType[] = [];
          if (dagDefinition?.schedule_config != null || suggestedScheduleConfig) triggerTypes.push('schedule');
          if (dagDefinition?.webhook_config != null || suggestedWebhookConfig) triggerTypes.push('webhook');
          if (triggerTypes.length === 0) return null;
          return (
            <>
              <Stack spacing={1.5} alignItems="center">
                {triggerTypes.map((t) => (
                  <TriggerNode key={t} triggerType={t} />
                ))}
              </Stack>
              {columns.length > 0 && <Arrow width={arrowWidth} />}
            </>
          );
        })()}
        {columns.map((colSteps, colIdx) => (
          <React.Fragment key={colIdx}>
            {/* Column: steps stacked vertically if parallel */}
            <Stack spacing={1.5} alignItems="center">
              {colSteps.map((step: any) => (
                <StepCard
                  key={step.id}
                  step={step}
                  bodySteps={loopBodies.get(step.id)}
                />
              ))}
            </Stack>
            {colIdx < columns.length - 1 && <Arrow width={arrowWidth} />}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default WorkflowVisualization;
